import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import type { Member, Organization, PlayEvent, PlayEventParticipant, PlayJoinPolicy, StudentProfile } from '@court-sync/shared';
import {
  findFirstReallocationRound,
  getPlayEventParticipantDisplayName,
  isPlayEventBoardFinished,
  isPlayEventBoardEndingSoon,
  isPlayEventBoardLive,
  participantTypesForJoinPolicy,
  playEventAsksSinglesPreferenceAtCheckIn,
  roundHasCompletedMatches,
} from '@court-sync/shared';
import toast from 'react-hot-toast';
import { PlayFeatureGate } from '@/components/play/PlayFeatureGate';
import { PlayEventDisplayBrandMark } from '@/components/play/PlayEventDisplayBrandMark';
import { PlayEventDisplayClock } from '@/components/play/PlayEventDisplayClock';
import { PlayEventDisplayShell } from '@/components/play/PlayEventDisplayShell';
import { playEventDisplayTheme } from '@/components/play/playEventDisplayTheme';
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui';
import { useCurrentClub } from '@/hooks/useCurrentClub';
import { useClubModuleAccess } from '@/hooks/useClubModuleAccess';
import { isApiError } from '@/services/apiClient';
import { playEventService, type PlayEventDetailResponse } from '@/services/playEvent';
import { PLAY_EVENT_STATUS_LABELS } from '@/lib/playEventLabels';
import { CalendarClock, Search, UserCheck, UserPlus, Users, X } from 'lucide-react';
import { organizationService } from '@/services/organization';
import { clubMembersService } from '@/services/clubMembers';
import { academyStudentService } from '@/services/academyStudentService';
import { isUuid } from '@/utils/uuid';
import { cn } from '@/lib/utils';
import { PlayEventParticipantRow } from '@/components/play/PlayEventParticipantAvatar';

const KIOSK_DIALOG_OVERLAY = 'bg-black/70 backdrop-blur-md';

const KIOSK_EVENT_FULL_MESSAGE =
  'This event is full. No more players can be registered, but you can still check in players who are already registered.';

function countActivePlayEventParticipants(participants: PlayEventParticipant[]): number {
  return participants.filter(
    (participant) => participant.status === 'registered' || participant.status === 'checked_in'
  ).length;
}

function isPlayEventAtCapacity(event: PlayEvent, participants: PlayEventParticipant[]): boolean {
  if (event.capacity == null) return false;
  return countActivePlayEventParticipants(participants) >= event.capacity;
}

function isEventCapacityFullError(error: unknown): boolean {
  return (
    isApiError(error) &&
    (error.code === 'CAPACITY_FULL' || /event is full/i.test(error.message))
  );
}

function kioskSegmentButtonClass(active: boolean): string {
  return cn(
    playEventDisplayTheme.kioskSegmentButton,
    active ? playEventDisplayTheme.tabActive : playEventDisplayTheme.tabInactive
  );
}

type KioskPlayerPanel = 'search' | 'guest';
type ParticipantGender = 'male' | 'female' | 'other';

function rosterSearchTypesForJoinPolicy(joinPolicy: PlayJoinPolicy): Array<'member' | 'student'> {
  return participantTypesForJoinPolicy(joinPolicy).filter(
    (type): type is 'member' | 'student' => type === 'member' || type === 'student'
  );
}

function memberDisplayName(member: Member): string {
  return `${member.firstName} ${member.lastName}`.trim();
}

function studentDisplayName(student: StudentProfile): string {
  return `${student.firstName} ${student.lastName}`.trim();
}

function splitNameQuery(query: string): { firstName: string; lastName: string } {
  const parts = query.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function participantMatchesName(participant: PlayEventParticipant, query: string): boolean {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return false;
  return getPlayEventParticipantDisplayName(participant).toLowerCase().includes(normalized);
}

function parseEventDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

function formatEventWindow(event: PlayEvent): string {
  const start = parseEventDate(event.startsAt);
  const end = parseEventDate(event.endsAt);
  return `${format(start, 'd MMM yyyy')} · ${format(start, 'HH:mm')} – ${format(end, 'HH:mm')}`;
}

function formatStartsIn(startsAt: Date | string, reference: Date): string | null {
  const start = parseEventDate(startsAt);
  const diffMs = start.getTime() - reference.getTime();
  if (diffMs <= 0) return null;

  const totalMinutes = Math.ceil(diffMs / 60_000);
  if (totalMinutes < 60) {
    return `Starts in ${totalMinutes} min`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (minutes === 0) return `Starts in ${hours} hr`;
  return `Starts in ${hours} hr ${minutes} min`;
}

function getKioskStatusLabel(event: PlayEvent, generatedRoundCount: number): string {
  const drawStarted = generatedRoundCount > 0;
  if (drawStarted && event.status === 'in_progress') {
    return PLAY_EVENT_STATUS_LABELS.in_progress;
  }
  if (event.status === 'in_progress' && !drawStarted) {
    return 'Ready to start';
  }
  return PLAY_EVENT_STATUS_LABELS[event.status];
}

export default function PlayEventKioskPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { currentClub } = useCurrentClub();
  const { hasClubManagement } = useClubModuleAccess();
  const [detail, setDetail] = useState<PlayEventDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const eventIdIsValid = isUuid(eventId);

  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestGender, setGuestGender] = useState<'male' | 'female' | 'other' | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [searchUseEmail, setSearchUseEmail] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [playerModalOpen, setPlayerModalOpen] = useState(false);
  const [playerPanel, setPlayerPanel] = useState<KioskPlayerPanel>('search');
  const [rosterSearchLoading, setRosterSearchLoading] = useState(false);
  const [rosterMemberResults, setRosterMemberResults] = useState<Member[]>([]);
  const [rosterStudentResults, setRosterStudentResults] = useState<StudentProfile[]>([]);
  const [pendingRosterMember, setPendingRosterMember] = useState<Member | null>(null);
  const [pendingRosterMemberGender, setPendingRosterMemberGender] = useState<
    '' | ParticipantGender
  >('');
  const [pendingRosterRegister, setPendingRosterRegister] = useState<
    | { type: 'member'; member: Member; gender?: ParticipantGender }
    | { type: 'student'; student: StudentProfile }
    | null
  >(null);
  const [checkInConfirm, setCheckInConfirm] = useState<
    | { type: 'participant'; participant: PlayEventParticipant }
    | { type: 'email'; email: string }
    | null
  >(null);
  const [guestPrefersSingles, setGuestPrefersSingles] = useState(false);
  const [organization, setOrganization] = useState<Organization | null>(null);

  const loadDetail = useCallback(async () => {
    if (!currentClub || !eventId || !eventIdIsValid) return;
    setLoading(true);
    try {
      const res = await playEventService.getEvent(currentClub.organizationId, eventId);
      if (res.success && res.data) {
        setDetail(res.data);
      }
    } finally {
      setLoading(false);
    }
  }, [currentClub, eventId, eventIdIsValid]);

  useEffect(() => {
    if (!currentClub) return undefined;
    void organizationService.getMyOrganization().then((res) => {
      if (res.success && res.data) {
        setOrganization(res.data);
      }
    });
    return undefined;
  }, [currentClub]);

  useEffect(() => {
    if (!currentClub || !eventId || !eventIdIsValid) return undefined;
    void loadDetail();
    const refreshInterval = window.setInterval(() => void loadDetail(), 30_000);
    return () => window.clearInterval(refreshInterval);
  }, [currentClub, eventId, eventIdIsValid, loadDetail]);

  useEffect(() => {
    const clockInterval = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(clockInterval);
  }, []);

  const event = detail?.event;
  const eventFinished = event ? isPlayEventBoardFinished(event, now) : false;
  const eventLive = event ? isPlayEventBoardLive(event, now) : false;
  const eventEndingSoon = event ? isPlayEventBoardEndingSoon(event, now) : false;
  const participants = detail?.participants ?? [];
  const checkedInParticipants = useMemo(
    () =>
      participants
        .filter((p) => p.status === 'checked_in')
        .sort((a, b) =>
          getPlayEventParticipantDisplayName(a).localeCompare(getPlayEventParticipantDisplayName(b))
        ),
    [participants]
  );
  const checkedInCount = checkedInParticipants.length;
  const participantCount = participants.length;
  const registeredAwaitingCheckInCount = useMemo(
    () => participants.filter((participant) => participant.status === 'registered').length,
    [participants]
  );
  const isEventFull = event ? isPlayEventAtCapacity(event, participants) : false;

  const generatedRoundCount = detail?.rounds.length ?? 0;
  const startsInLabel = useMemo(() => {
    if (!event) return null;
    if (event.status === 'completed' || event.status === 'cancelled') return null;
    return formatStartsIn(event.startsAt, now);
  }, [event, now]);
  const kioskStatusLabel = event ? getKioskStatusLabel(event, generatedRoundCount) : null;
  const nextSitOutRound =
    event && detail
      ? findFirstReallocationRound(
          detail.rounds.map((round) => ({
            roundNumber: round.roundNumber,
            status: round.status,
            hasCompletedMatches: roundHasCompletedMatches(detail.matches, round.roundNumber),
          })),
          event.roundCount ?? 0
        )
      : null;
  const canSitOutPlayers = event?.status === 'in_progress' && nextSitOutRound != null;
  const askSinglesPreference = event ? playEventAsksSinglesPreferenceAtCheckIn(event) : false;
  const kioskRegistrationEnabled = event?.kioskRegistrationEnabled ?? false;
  const kioskGuestRegistrationEnabled = event?.kioskGuestRegistrationEnabled ?? false;
  const rosterSearchTypes = useMemo(() => {
    if (!event) return [];
    const types = rosterSearchTypesForJoinPolicy(event.joinPolicy);
    if (!hasClubManagement) {
      return types.filter((type) => type !== 'member');
    }
    return types;
  }, [event?.joinPolicy, hasClubManagement]);
  const canRegisterGuests =
    kioskRegistrationEnabled &&
    kioskGuestRegistrationEnabled &&
    event != null &&
    participantTypesForJoinPolicy(event.joinPolicy).includes('guest');
  const registeredMemberIds = useMemo(
    () =>
      new Set(
        participants
          .filter((participant) => participant.memberId && participant.status !== 'withdrawn')
          .map((participant) => participant.memberId as string)
      ),
    [participants]
  );
  const registeredStudentIds = useMemo(
    () =>
      new Set(
        participants
          .filter((participant) => participant.studentId && participant.status !== 'withdrawn')
          .map((participant) => participant.studentId as string)
      ),
    [participants]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchDebounced(searchQuery.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (
      !playerModalOpen ||
      !kioskRegistrationEnabled ||
      searchUseEmail ||
      playerPanel !== 'search'
    ) {
      return undefined;
    }
    if (!currentClub || !event || searchDebounced.length < 1) {
      setRosterMemberResults([]);
      setRosterStudentResults([]);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setRosterSearchLoading(true);
      try {
        const memberPromise = rosterSearchTypes.includes('member')
          ? clubMembersService.listMembers(currentClub.id, {
              page: 1,
              limit: 8,
              search: searchDebounced,
              isActive: true,
            })
          : Promise.resolve(null);
        const studentPromise =
          rosterSearchTypes.includes('student') && event.academyId
            ? academyStudentService.getAcademyStudentsPaginated(event.academyId, 1, 8, {
                searchQuery: searchDebounced,
                isActive: true,
              })
            : Promise.resolve(null);

        const [membersRes, studentsRes] = await Promise.all([memberPromise, studentPromise]);
        if (cancelled) return;

        const members =
          membersRes && membersRes.success && membersRes.data
            ? membersRes.data.filter((member) => !registeredMemberIds.has(member.id))
            : [];
        const students =
          studentsRes?.data?.filter((student) => !registeredStudentIds.has(student.id)) ?? [];

        setRosterMemberResults(members.slice(0, 8));
        setRosterStudentResults(students.slice(0, 8));
      } finally {
        if (!cancelled) setRosterSearchLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [
    currentClub,
    event,
    kioskRegistrationEnabled,
    playerModalOpen,
    playerPanel,
    searchDebounced,
    searchUseEmail,
    registeredMemberIds,
    registeredStudentIds,
    rosterSearchTypes,
  ]);

  const registeredNameResults = useMemo(() => {
    if (searchUseEmail || !searchQuery.trim()) return [];
    return participants
      .filter((participant) => participant.status === 'registered')
      .filter((participant) => participantMatchesName(participant, searchQuery))
      .slice(0, 8);
  }, [participants, searchQuery, searchUseEmail]);

  const resetPlayerModal = () => {
    setSearchQuery('');
    setSearchDebounced('');
    setSearchUseEmail(false);
    setSearchEmail('');
    setPlayerPanel('search');
    setRosterMemberResults([]);
    setRosterStudentResults([]);
    setPendingRosterMember(null);
    setPendingRosterMemberGender('');
    setPendingRosterRegister(null);
    setCheckInConfirm(null);
    setGuestPrefersSingles(false);
    setGuestFirstName('');
    setGuestLastName('');
    setGuestEmail('');
    setGuestPhone('');
    setGuestGender('');
  };

  const handleOpenPlayerModal = () => {
    resetPlayerModal();
    if (canRegisterGuests && rosterSearchTypes.length === 0 && !isEventFull) {
      setPlayerPanel('guest');
    }
    setPlayerModalOpen(true);
  };

  const handleOpenGuestPanel = () => {
    if (isEventFull) {
      toast.error(KIOSK_EVENT_FULL_MESSAGE);
      return;
    }
    const { firstName, lastName } = splitNameQuery(searchQuery);
    setGuestFirstName(firstName);
    setGuestLastName(lastName);
    setPlayerPanel('guest');
  };

  const registerRosterParticipant = async (
    input:
      | { type: 'member'; member: Member; gender?: ParticipantGender }
      | { type: 'student'; student: StudentProfile },
    prefersSingles?: boolean
  ) => {
    if (!currentClub || !eventId || !event) return;
    if (isEventFull) {
      toast.error(KIOSK_EVENT_FULL_MESSAGE);
      return;
    }
    setActionLoading(true);
    try {
      const res = await playEventService.addKioskParticipant(currentClub.organizationId, eventId, {
        participantType: input.type,
        ...(input.type === 'member'
          ? {
              memberId: input.member.id,
              ...(input.gender ? { gender: input.gender } : {}),
            }
          : { studentId: input.student.id }),
        checkIn: true,
        comped: event.paymentMode === 'at_door',
        ...(askSinglesPreference && prefersSingles !== undefined
          ? { prefersSingles }
          : {}),
      });
      if (res.success) {
        toast.success('Registered and checked in');
        resetPlayerModal();
        setPlayerModalOpen(false);
        await loadDetail();
      } else {
        toast.error(res.error ?? 'Failed to register');
      }
    } catch (error) {
      if (isEventCapacityFullError(error)) {
        toast.error(KIOSK_EVENT_FULL_MESSAGE);
      } else if (isApiError(error)) {
        toast.error(error.message);
      } else {
        toast.error('Failed to register');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleSelectMemberForRegister = (member: Member) => {
    if (!member.gender) {
      setPendingRosterMember(member);
      setPendingRosterMemberGender('');
      return;
    }
    const payload = { type: 'member' as const, member };
    if (askSinglesPreference) {
      setPendingRosterRegister(payload);
      return;
    }
    void registerRosterParticipant(payload);
  };

  const handleConfirmMemberGenderRegister = () => {
    if (!pendingRosterMember || !pendingRosterMemberGender) {
      toast.error('Gender is required');
      return;
    }
    const payload = {
      type: 'member' as const,
      member: pendingRosterMember,
      gender: pendingRosterMemberGender,
    };
    if (askSinglesPreference) {
      setPendingRosterMember(null);
      setPendingRosterMemberGender('');
      setPendingRosterRegister(payload);
      return;
    }
    void registerRosterParticipant(payload);
  };

  const handleSelectStudentForRegister = (student: StudentProfile) => {
    const payload = { type: 'student' as const, student };
    if (askSinglesPreference) {
      setPendingRosterRegister(payload);
      return;
    }
    void registerRosterParticipant(payload);
  };

  const completeRosterRegister = async (prefersSingles?: boolean) => {
    if (!pendingRosterRegister) return;
    await registerRosterParticipant(pendingRosterRegister, prefersSingles);
    setPendingRosterRegister(null);
  };

  const handleGuestRegister = async (formEvent: React.FormEvent) => {
    formEvent.preventDefault();
    if (!currentClub || !eventId) return;
    if (isEventFull) {
      toast.error(KIOSK_EVENT_FULL_MESSAGE);
      return;
    }
    if (!guestFirstName.trim() || !guestLastName.trim()) {
      toast.error('First and last name are required');
      return;
    }
    if (!guestEmail.trim() && !guestPhone.trim()) {
      toast.error('Email or phone is required');
      return;
    }
    if (!guestGender) {
      toast.error('Gender is required');
      return;
    }
    setActionLoading(true);
    try {
      const res = await playEventService.addKioskParticipant(currentClub.organizationId, eventId, {
        participantType: 'guest',
        guestFirstName: guestFirstName.trim(),
        guestLastName: guestLastName.trim(),
        contactEmail: guestEmail.trim() || undefined,
        contactPhone: guestPhone.trim() || undefined,
        gender: guestGender,
        checkIn: true,
        comped: event?.paymentMode === 'at_door',
        ...(askSinglesPreference ? { prefersSingles: guestPrefersSingles } : {}),
      });
      if (res.success) {
        toast.success('Guest registered and checked in');
        setGuestFirstName('');
        setGuestLastName('');
        setGuestEmail('');
        setGuestPhone('');
        setGuestGender('');
        setPlayerModalOpen(false);
        resetPlayerModal();
        await loadDetail();
      } else {
        toast.error(res.error ?? 'Failed to register guest');
      }
    } catch (error) {
      if (isEventCapacityFullError(error)) {
        toast.error(KIOSK_EVENT_FULL_MESSAGE);
      } else if (isApiError(error)) {
        toast.error(error.message);
      } else {
        toast.error('Failed to register guest');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const performCheckIn = async (
    target:
      | { type: 'participant'; participant: PlayEventParticipant }
      | { type: 'email'; email: string },
    prefersSingles?: boolean
  ) => {
    if (!currentClub || !eventId) return;
    setActionLoading(true);
    try {
      const checkInOptions =
        prefersSingles !== undefined ? { prefersSingles } : undefined;

      if (target.type === 'participant') {
        const res = await playEventService.checkInParticipant(
          currentClub.organizationId,
          eventId,
          target.participant.id,
          checkInOptions
        );
        if (!res.success) {
          toast.error(res.error ?? 'Failed to check in');
          return;
        }
      } else {
        const res = await playEventService.kioskCheckIn(currentClub.organizationId, eventId, {
          contactEmail: target.email,
          ...checkInOptions,
        });
        if (!res.success) {
          toast.error(res.error ?? 'Registration not found');
          return;
        }
      }

      toast.success('Checked in');
      resetPlayerModal();
      setPlayerModalOpen(false);
      await loadDetail();
    } finally {
      setActionLoading(false);
    }
  };

  const completeCheckIn = async (prefersSingles?: boolean) => {
    if (!checkInConfirm) return;
    await performCheckIn(checkInConfirm, prefersSingles);
  };

  const handleSelectParticipantForCheckIn = (participant: PlayEventParticipant) => {
    if (askSinglesPreference) {
      setCheckInConfirm({ type: 'participant', participant });
      return;
    }
    void performCheckIn({ type: 'participant', participant });
  };

  const handleKioskCheckInByEmail = (formEvent: React.FormEvent) => {
    formEvent.preventDefault();
    if (!searchEmail.trim()) {
      toast.error('Enter the email used at registration');
      return;
    }
    if (askSinglesPreference) {
      setCheckInConfirm({ type: 'email', email: searchEmail.trim() });
      return;
    }
    void performCheckIn({ type: 'email', email: searchEmail.trim() });
  };

  const kioskModalDescription = useMemo(() => {
    if (playerPanel === 'guest') {
      return 'Add a walk-in guest and check them in.';
    }
    if (searchUseEmail) {
      return 'Enter the email used when they registered.';
    }
    if (kioskRegistrationEnabled && canRegisterGuests) {
      return 'Search by name to check in or register players for today.';
    }
    if (kioskRegistrationEnabled) {
      return 'Search by name to check in or register eligible members and students.';
    }
    return 'Search by name or switch to email lookup.';
  }, [canRegisterGuests, kioskRegistrationEnabled, playerPanel, searchUseEmail]);

  const hasUnifiedSearchResults =
    registeredNameResults.length > 0 ||
    rosterMemberResults.length > 0 ||
    rosterStudentResults.length > 0;

  const checkInConfirmName =
    checkInConfirm?.type === 'participant'
      ? getPlayEventParticipantDisplayName(checkInConfirm.participant)
      : checkInConfirm?.type === 'email'
        ? checkInConfirm.email
        : null;

  const handleSitOut = async (participantId: string) => {
    if (!currentClub || !eventId || nextSitOutRound == null) return;
    setActionLoading(true);
    try {
      const res = await playEventService.socialWithdraw(
        currentClub.organizationId,
        eventId,
        participantId,
        nextSitOutRound
      );
      if (res.success) {
        toast.success(`Marked sit-out for round ${nextSitOutRound}`);
        await loadDetail();
      } else {
        toast.error(res.error ?? 'Failed to record sit-out');
      }
    } finally {
      setActionLoading(false);
    }
  };

  if (!eventIdIsValid) {
    return <Navigate to="/admin/play-events" replace />;
  }

  return (
    <PlayFeatureGate>
      <PlayEventDisplayShell
        primaryColor={organization?.primaryColor}
        secondaryColor={organization?.secondaryColor}
      >
        <header className={playEventDisplayTheme.header}>
          <div className="mx-auto flex max-w-7xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3 sm:items-center sm:gap-4">
              <PlayEventDisplayBrandMark
                logoUrl={organization?.logoUrl}
                organizationName={organization?.name}
              />
              <div className="min-w-0">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <p className={playEventDisplayTheme.accentLabel}>Event kiosk</p>
                  {event && kioskStatusLabel ? (
                    <>
                      <span className={playEventDisplayTheme.badge}>{kioskStatusLabel}</span>
                      {startsInLabel ? (
                        <span className={playEventDisplayTheme.accentBadge}>{startsInLabel}</span>
                      ) : null}
                    </>
                  ) : null}
                </div>
                <h1 className="truncate text-2xl font-bold tracking-tight sm:text-3xl">
                  {event?.name ?? 'Play event'}
                </h1>
                {event ? (
                  <p className={`mt-1 flex items-center gap-1.5 ${playEventDisplayTheme.muted}`}>
                    <CalendarClock className="h-4 w-4 shrink-0" aria-hidden />
                    {formatEventWindow(event)}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3">
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className={`${playEventDisplayTheme.outlineButton} ${playEventDisplayTheme.kioskActionButton}`}
                  onClick={handleOpenPlayerModal}
                >
                  <UserCheck className="mr-2 h-5 w-5" aria-hidden />
                  Check in
                </Button>
              </div>
              <PlayEventDisplayClock
                now={now}
                eventLive={eventLive}
                eventFinished={eventFinished}
                eventEndingSoon={eventEndingSoon}
              />
              {eventId ? (
                <Link
                  to={`/admin/play-events/${eventId}`}
                  className={playEventDisplayTheme.iconButton}
                  aria-label="Close kiosk and return to event"
                >
                  <X className="h-5 w-5" aria-hidden />
                </Link>
              ) : null}
            </div>
          </div>
        </header>

        <Dialog
          open={playerModalOpen}
          onOpenChange={(open) => {
            setPlayerModalOpen(open);
            if (!open) resetPlayerModal();
          }}
        >
          <DialogContent
            overlayClassName={KIOSK_DIALOG_OVERLAY}
            className={cn(playEventDisplayTheme.dialog, playEventDisplayTheme.kioskDialog)}
          >
            <DialogHeader className="space-y-2">
              <DialogTitle className={playEventDisplayTheme.kioskDialogTitle}>Check in</DialogTitle>
              <DialogDescription
                className={cn(
                  playEventDisplayTheme.dialogDescription,
                  playEventDisplayTheme.kioskDialogDescription
                )}
              >
                {kioskModalDescription}
              </DialogDescription>
            </DialogHeader>

            {kioskRegistrationEnabled && isEventFull ? (
              <div
                className={cn(
                  playEventDisplayTheme.kioskPanel,
                  'border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100'
                )}
                role="status"
              >
                <p className="text-base font-semibold">Event is full</p>
                <p className={`mt-1 text-base ${playEventDisplayTheme.muted}`}>
                  No more players can be registered. You can still check in players who are already
                  registered.
                </p>
              </div>
            ) : null}

            {pendingRosterRegister && askSinglesPreference ? (
              <div className={playEventDisplayTheme.kioskBodySpacing}>
                <div className={playEventDisplayTheme.kioskPanel}>
                  <p className={playEventDisplayTheme.kioskSectionLabel}>
                    Registering & checking in
                  </p>
                  <p className="mt-1 text-lg font-medium">
                    {pendingRosterRegister.type === 'member'
                      ? memberDisplayName(pendingRosterRegister.member)
                      : studentDisplayName(pendingRosterRegister.student)}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-base font-medium">Would you like to play singles?</p>
                  <p className={`text-base ${playEventDisplayTheme.muted}`}>
                    This event prefers doubles but may use singles when player numbers do not fit evenly.
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    className={cn('flex-1', playEventDisplayTheme.kioskAction)}
                    isLoading={actionLoading}
                    onClick={() => void completeRosterRegister(true)}
                  >
                    Yes, singles
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      'flex-1',
                      playEventDisplayTheme.kioskAction,
                      playEventDisplayTheme.outlineButton
                    )}
                    isLoading={actionLoading}
                    onClick={() => void completeRosterRegister(false)}
                  >
                    Prefer doubles
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  className={cn(
                    'h-12 w-full text-base',
                    playEventDisplayTheme.muted,
                    'hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-white/5 dark:hover:text-white'
                  )}
                  onClick={() => setPendingRosterRegister(null)}
                >
                  Back
                </Button>
              </div>
            ) : pendingRosterMember ? (
              <div className={playEventDisplayTheme.kioskBodySpacing}>
                <div className={playEventDisplayTheme.kioskPanel}>
                  <p className={playEventDisplayTheme.kioskSectionLabel}>Member gender required</p>
                  <p className="mt-1 text-lg font-medium">{memberDisplayName(pendingRosterMember)}</p>
                </div>
                <Select
                  value={pendingRosterMemberGender}
                  onValueChange={(value) =>
                    setPendingRosterMemberGender(value as ParticipantGender)
                  }
                >
                  <SelectTrigger className={playEventDisplayTheme.kioskField}>
                    <SelectValue placeholder="Gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  className={cn('w-full', playEventDisplayTheme.kioskAction)}
                  isLoading={actionLoading}
                  onClick={handleConfirmMemberGenderRegister}
                >
                  Continue
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className={cn('h-12 w-full text-base', playEventDisplayTheme.muted)}
                  onClick={() => {
                    setPendingRosterMember(null);
                    setPendingRosterMemberGender('');
                  }}
                >
                  Back
                </Button>
              </div>
            ) : checkInConfirm && askSinglesPreference ? (
              <div className={playEventDisplayTheme.kioskBodySpacing}>
                <div className={playEventDisplayTheme.kioskPanel}>
                  <p className={playEventDisplayTheme.kioskSectionLabel}>Checking in</p>
                  <p className="mt-1 text-lg font-medium">{checkInConfirmName}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-base font-medium">Would you like to play singles?</p>
                  <p className={`text-base ${playEventDisplayTheme.muted}`}>
                    This event prefers doubles but may use singles when player numbers do not fit evenly.
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    className={cn('flex-1', playEventDisplayTheme.kioskAction)}
                    isLoading={actionLoading}
                    onClick={() => void completeCheckIn(true)}
                  >
                    Yes, singles
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      'flex-1',
                      playEventDisplayTheme.kioskAction,
                      playEventDisplayTheme.outlineButton
                    )}
                    isLoading={actionLoading}
                    onClick={() => void completeCheckIn(false)}
                  >
                    Prefer doubles
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  className={cn(
                    'h-12 w-full text-base',
                    playEventDisplayTheme.muted,
                    'hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-white/5 dark:hover:text-white'
                  )}
                  onClick={() => setCheckInConfirm(null)}
                >
                  Back
                </Button>
              </div>
            ) : playerPanel === 'guest' && canRegisterGuests ? (
              <div className={playEventDisplayTheme.kioskBodySpacing}>
                {isEventFull ? (
                  <div className={playEventDisplayTheme.kioskPanel}>
                    <p className="text-base font-medium">Event is full</p>
                    <p className={`mt-1 text-base ${playEventDisplayTheme.muted}`}>
                      Walk-in guest registration is unavailable because this event has reached its
                      capacity.
                    </p>
                  </div>
                ) : (
                <form className={playEventDisplayTheme.kioskBodySpacing} onSubmit={(e) => void handleGuestRegister(e)}>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Input
                      value={guestFirstName}
                      onChange={(e) => setGuestFirstName(e.target.value)}
                      placeholder="First name"
                      className={playEventDisplayTheme.kioskField}
                    />
                    <Input
                      value={guestLastName}
                      onChange={(e) => setGuestLastName(e.target.value)}
                      placeholder="Last name"
                      className={playEventDisplayTheme.kioskField}
                    />
                  </div>
                  <Input
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="Email"
                    type="email"
                    className={playEventDisplayTheme.kioskField}
                  />
                  <Input
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    placeholder="Phone"
                    className={playEventDisplayTheme.kioskField}
                  />
                  <Select
                    value={guestGender}
                    onValueChange={(value) => setGuestGender(value as typeof guestGender)}
                  >
                    <SelectTrigger className={playEventDisplayTheme.kioskField}>
                      <SelectValue placeholder="Gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {askSinglesPreference ? (
                    <div className={playEventDisplayTheme.kioskPanel}>
                      <p className="text-base font-medium">Singles preference</p>
                      <p className={`mt-1 text-base ${playEventDisplayTheme.muted}`}>
                        If we cannot fill a doubles court, would this player like a singles match?
                      </p>
                      <div className="mt-3 flex gap-3">
                        <Button
                          type="button"
                          variant={guestPrefersSingles ? 'default' : 'outline'}
                          className={cn(
                            'flex-1',
                            playEventDisplayTheme.kioskAction,
                            !guestPrefersSingles && playEventDisplayTheme.outlineButton
                          )}
                          onClick={() => setGuestPrefersSingles(true)}
                        >
                          Yes, singles
                        </Button>
                        <Button
                          type="button"
                          variant={!guestPrefersSingles ? 'default' : 'outline'}
                          className={cn(
                            'flex-1',
                            playEventDisplayTheme.kioskAction,
                            guestPrefersSingles && playEventDisplayTheme.outlineButton
                          )}
                          onClick={() => setGuestPrefersSingles(false)}
                        >
                          Prefer doubles
                        </Button>
                      </div>
                    </div>
                  ) : null}
                  <Button
                    type="submit"
                    className={cn('w-full', playEventDisplayTheme.kioskAction)}
                    isLoading={actionLoading}
                  >
                    Register & check in
                  </Button>
                </form>
                )}
                {rosterSearchTypes.length > 0 || kioskRegistrationEnabled ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className={cn('h-12 w-full text-base', playEventDisplayTheme.muted)}
                    onClick={() => setPlayerPanel('search')}
                  >
                    Back to search
                  </Button>
                ) : null}
              </div>
            ) : (
              <div className={playEventDisplayTheme.kioskBodySpacing}>
                <div
                  className={playEventDisplayTheme.kioskSegmentList}
                  role="group"
                  aria-label="Search by name or email"
                >
                  <button
                    type="button"
                    className={kioskSegmentButtonClass(!searchUseEmail)}
                    onClick={() => setSearchUseEmail(false)}
                  >
                    Name
                  </button>
                  <button
                    type="button"
                    className={kioskSegmentButtonClass(searchUseEmail)}
                    onClick={() => setSearchUseEmail(true)}
                  >
                    Email
                  </button>
                </div>

                {searchUseEmail ? (
                  <form className={playEventDisplayTheme.kioskBodySpacing} onSubmit={(e) => void handleKioskCheckInByEmail(e)}>
                    <Input
                      value={searchEmail}
                      onChange={(e) => setSearchEmail(e.target.value)}
                      placeholder="Email used at registration"
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      className={playEventDisplayTheme.kioskField}
                    />
                    <Button
                      type="submit"
                      className={cn('w-full', playEventDisplayTheme.kioskAction)}
                      isLoading={actionLoading}
                    >
                      Check in
                    </Button>
                  </form>
                ) : (
                  <div className={playEventDisplayTheme.kioskBodySpacing}>
                    <div className="relative">
                      <Search className={playEventDisplayTheme.kioskSearchIcon} />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={
                          kioskRegistrationEnabled && rosterSearchTypes.length > 0
                            ? rosterSearchTypes.includes('member') &&
                              rosterSearchTypes.includes('student')
                              ? 'Search players, members, or students'
                              : rosterSearchTypes.includes('member')
                                ? 'Search players or members'
                                : 'Search players or students'
                            : 'Search by name'
                        }
                        autoComplete="off"
                        autoCapitalize="words"
                        className={cn(
                          playEventDisplayTheme.kioskField,
                          playEventDisplayTheme.kioskFieldWithIcon
                        )}
                      />
                    </div>

                    {searchQuery.trim() ? (
                      rosterSearchLoading && kioskRegistrationEnabled ? (
                        <p className={playEventDisplayTheme.kioskHint}>Searching…</p>
                      ) : hasUnifiedSearchResults ? (
                        <div className={playEventDisplayTheme.kioskResultsList}>
                          {registeredNameResults.length > 0 ? (
                            <div className="space-y-3">
                              <p className={playEventDisplayTheme.kioskSectionLabel}>
                                Registered — check in
                              </p>
                              <ul className="space-y-2">
                                {registeredNameResults.map((participant) => (
                                  <li key={participant.id}>
                                    <button
                                      type="button"
                                      className={playEventDisplayTheme.kioskResultRow}
                                      disabled={actionLoading}
                                      onClick={() => handleSelectParticipantForCheckIn(participant)}
                                    >
                                      <PlayEventParticipantRow participant={participant} size="md" />
                                      <UserCheck
                                        className={cn('h-5 w-5 shrink-0', playEventDisplayTheme.accentText)}
                                        aria-hidden
                                      />
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : null}

                          {kioskRegistrationEnabled &&
                          !isEventFull &&
                          (rosterMemberResults.length > 0 || rosterStudentResults.length > 0) ? (
                            <div className="space-y-3">
                              <p className={playEventDisplayTheme.kioskSectionLabel}>
                                Register & check in
                              </p>
                              <ul className="space-y-2">
                                {rosterMemberResults.map((member) => (
                                  <li key={member.id}>
                                    <button
                                      type="button"
                                      className={playEventDisplayTheme.kioskResultRow}
                                      disabled={actionLoading}
                                      onClick={() => handleSelectMemberForRegister(member)}
                                    >
                                      <span className="font-medium">{memberDisplayName(member)}</span>
                                      <span
                                        className={cn(
                                          'flex items-center gap-1.5 text-sm',
                                          playEventDisplayTheme.mutedFaint
                                        )}
                                      >
                                        <UserPlus className="h-4 w-4" aria-hidden />
                                        Member
                                      </span>
                                    </button>
                                  </li>
                                ))}
                                {rosterStudentResults.map((student) => (
                                  <li key={student.id}>
                                    <button
                                      type="button"
                                      className={playEventDisplayTheme.kioskResultRow}
                                      disabled={actionLoading}
                                      onClick={() => handleSelectStudentForRegister(student)}
                                    >
                                      <span className="font-medium">{studentDisplayName(student)}</span>
                                      <span
                                        className={cn(
                                          'flex items-center gap-1.5 text-sm',
                                          playEventDisplayTheme.mutedFaint
                                        )}
                                      >
                                        <UserPlus className="h-4 w-4" aria-hidden />
                                        Student
                                      </span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <div className={cn(playEventDisplayTheme.kioskPanel, 'space-y-3')}>
                          <p className={`text-base ${playEventDisplayTheme.muted}`}>
                            No players match &ldquo;{searchQuery.trim()}&rdquo;.
                          </p>
                          {canRegisterGuests && !isEventFull ? (
                            <Button
                              type="button"
                              variant="outline"
                              className={cn(
                                'w-full',
                                playEventDisplayTheme.kioskAction,
                                playEventDisplayTheme.outlineButton
                              )}
                              onClick={handleOpenGuestPanel}
                            >
                              <UserPlus className="mr-2 h-5 w-5" aria-hidden />
                              Register as walk-in guest
                            </Button>
                          ) : null}
                        </div>
                      )
                    ) : (
                      <p className={playEventDisplayTheme.kioskHint}>
                        {kioskRegistrationEnabled
                          ? 'Start typing to check in or register players for today.'
                          : 'Start typing a player\u2019s name to check them in.'}
                      </p>
                    )}

                    {canRegisterGuests &&
                    !isEventFull &&
                    rosterSearchTypes.length > 0 &&
                    searchQuery.trim() === '' ? (
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          'w-full',
                          playEventDisplayTheme.kioskAction,
                          playEventDisplayTheme.outlineButton
                        )}
                        onClick={handleOpenGuestPanel}
                      >
                        <UserPlus className="mr-2 h-5 w-5" aria-hidden />
                        Register walk-in guest
                      </Button>
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <main className={playEventDisplayTheme.main}>
          <section className={playEventDisplayTheme.section}>
            <div className="mb-4 flex shrink-0 items-center justify-between gap-2">
              <div className={`flex items-center gap-2 ${playEventDisplayTheme.sectionHeading}`}>
                <Users className="h-4 w-4" aria-hidden />
                Checked in
              </div>
              <p className={`text-sm tabular-nums ${playEventDisplayTheme.muted}`}>
                {checkedInCount} / {participantCount}
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              {loading ? (
                <div className="flex min-h-full items-center justify-center py-8" aria-busy="true">
                  <div className={playEventDisplayTheme.spinner} />
                </div>
              ) : checkedInParticipants.length === 0 ? (
                <div className="flex min-h-full flex-col items-center justify-center px-4 py-8 text-center">
                  <p className={playEventDisplayTheme.bodyText}>No players checked in yet.</p>
                  <p className={`mt-2 text-sm ${playEventDisplayTheme.mutedFaint}`}>
                    {registeredAwaitingCheckInCount > 0 ? (
                      <>
                        {registeredAwaitingCheckInCount}{' '}
                        {registeredAwaitingCheckInCount === 1 ? 'player is' : 'players are'} registered
                        and waiting. Use Check in to find them
                        {kioskRegistrationEnabled ? ' or register day-of arrivals' : ''}.
                      </>
                    ) : (
                      <>
                        Use Check in to find registered players
                        {kioskRegistrationEnabled ? ' or register day-of arrivals' : ''}.
                      </>
                    )}
                  </p>
                </div>
              ) : (
                <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {checkedInParticipants.map((participant) => (
                    <li
                      key={participant.id}
                      className={`${playEventDisplayTheme.listItem} min-w-0`}
                    >
                      <div className="min-w-0 flex-1">
                        <PlayEventParticipantRow participant={participant} size="sm" />
                      </div>
                      {canSitOutPlayers ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className={`shrink-0 px-2 text-xs ${playEventDisplayTheme.outlineButton}`}
                          isLoading={actionLoading}
                          onClick={() => void handleSitOut(participant.id)}
                        >
                          Sit out
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {canSitOutPlayers ? (
              <p className={`mt-4 shrink-0 pt-3 text-xs ${playEventDisplayTheme.divider} ${playEventDisplayTheme.mutedFaint}`}>
                Sit-out applies to round {nextSitOutRound}.
              </p>
            ) : null}
          </section>
        </main>
      </PlayEventDisplayShell>
    </PlayFeatureGate>
  );
}
